import { useEffect, useMemo, useRef } from "react";

import Header from "@/template-service/components/Header";
import Footer from "@/template-service/components/Footer";
import MobileStickyCTA from "@/template-service/components/MobileStickyCTA";
import HomePage from "@/template-service/pages/Home";
import ServicesPage from "@/template-service/pages/Services";
import AboutPage from "@/template-service/pages/About";
import ConsultationsPage from "@/template-service/pages/Consultations";
import ReviewsPage from "@/template-service/pages/Reviews";
import FAQPage from "@/template-service/pages/FAQ";
import ContactPage from "@/template-service/pages/Contact";
import { ServiceBasePathProvider } from "@/template-service/lib/route";
import { setServiceData, RUNTIME } from "@/template-service/lib/_data";

/** Map incoming pageSlug → template Page component. Anything unknown falls
 * back to Home so a stale link never 404s the shell. */
const PAGE_MAP = {
  "": HomePage,
  home: HomePage,
  services: ServicesPage,
  about: AboutPage,
  consultations: ConsultationsPage,
  reviews: ReviewsPage,
  faq: FAQPage,
  contact: ContactPage,
};

/**
 * Shell that renders the Standard Service Template for any published site with
 * `industry_template === "service"`.
 *
 * The parent (`PublicSite.jsx`) has already fetched the site data and includes
 * a `service_config` payload derived from the business's stored fields. We:
 *   1. Mutate the template's `_data` module in place with real content BEFORE
 *      the first render (via `useMemo` so it happens synchronously in render,
 *      not in an effect — no visible flash of hard-coded defaults).
 *   2. Wrap the template chrome in a `ServiceBasePathProvider` so every
 *      `<Link to="/consultations" />` inside the template routes to
 *      `/site/<slug>/consultations` on the real browser URL.
 *   3. Pick the correct Page component based on the incoming `pageSlug` and
 *      render Header → Page → Footer → MobileStickyCTA — the same DOM shape
 *      as the template's original `Layout` component, minus the standalone
 *      `<Outlet />` (which we don't need since we render the Page directly).
 */
export default function PublicServiceShell({ slug, pageSlug, serviceConfig }) {
  const configRef = useRef(null);

  // Sync the template module state *during* render (before children mount).
  // useMemo runs synchronously on the render pass whose deps change — perfect
  // for one-shot mutation before Header/Footer/Page read from `_data`.
  useMemo(() => {
    RUNTIME.slug = slug || "";
    if (serviceConfig && configRef.current !== serviceConfig) {
      setServiceData(serviceConfig);
      configRef.current = serviceConfig;
    }
  }, [serviceConfig, slug]);

  // Reset scroll when the page changes — matches Layout.jsx behaviour.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pageSlug]);

  const Page = PAGE_MAP[(pageSlug || "").toLowerCase()] || HomePage;
  const basePath = `/site/${slug}`;

  return (
    <ServiceBasePathProvider basePath={basePath}>
      <div className="min-h-screen bg-[#f9f6f0] text-[#2c2a29] font-body" data-testid="public-service-shell" data-published-site="1">
        <Header />
        <main data-testid="main-content">
          <Page />
        </main>
        <Footer />
        <MobileStickyCTA />
      </div>
    </ServiceBasePathProvider>
  );
}
